class GlobalProductQuiz extends HTMLElement {
  constructor() {
    super();
    this.recommendationsHandles = [];
  }

  /**
   * Called when the element is added to the DOM.
   * Sets up initial state and event listeners.
   */
  connectedCallback() {
    this.loadRecommendationsHandles();
    this.options = this.querySelector(".global-product-quiz--options");
    this.defaultOptionRecommendation = this.querySelector(
      `.global-product-quiz--checkbox[data-default-option-recommendation]`
    ).value;
    this.options.addEventListener("input", this.optionsEventHandler.bind(this));

    window.addEventListener("hashchange", this.handleHashChange.bind(this));
    this.handleHashChange(); // Check the hash on initial load
  }

  /**
   * Called when the element is removed from the DOM.
   * Cleans up event listeners.
   */
  disconnectedCallback() {
    this.options.removeEventListener(
      "input",
      this.optionsEventHandler.bind(this)
    );
    window.removeEventListener("hashchange", this.handleHashChange.bind(this));
  }

  /**
   * Handles the input event on quiz options.
   * Determines and displays the appropriate recommendation based on the selected options.
   */
  optionsEventHandler() {
    const selectedOptions = this.getSelectedOptions();
    this.highlightOptions();
    if (selectedOptions.length === 0) {
      this.resetVisibleRecommendation();
      return;
    }

    if (selectedOptions.length === 1) {
      this.showRecommendation(selectedOptions[0]);
      return;
    }

    if (
      selectedOptions.includes(this.defaultOptionRecommendation) ||
      selectedOptions.length > 2
    ) {
      this.showRecommendation(this.defaultOptionRecommendation);
      return;
    }

    const optionsHandle = selectedOptions.join("-");
    const validRecommendationHandle =
      this.getValidRecommendationHandle(optionsHandle);
    this.showRecommendation(validRecommendationHandle);
  }

  /**
   * Loads all recommendation handles from the quiz recommendations items.
   */
  loadRecommendationsHandles() {
    this.recommendationsHandles = Array.from(
      this.querySelectorAll(".global-product-quiz--recommendation-item")
    ).map((el) => el.dataset.productQuizRecommendationHandle);
  }

  /**
   * Gets a valid recommendation handle based on the selected options.
   *
   * @param {string} optionsHandle A concatenated string of selected options.
   * @returns {string} A valid recommendation handle.
   */
  getValidRecommendationHandle(optionsHandle) {
    if (this.recommendationsHandles.includes(optionsHandle))
      return optionsHandle;

    const sortedOptionHandle = optionsHandle.split("-").sort().join("-");

    return this.recommendationsHandles.includes(sortedOptionHandle)
      ? sortedOptionHandle
      : this.defaultOptionRecommendation;
  }

  /**
   * Retrieves all selected options in the quiz.
   *
   * @returns {string[]} An array of selected option values.
   */
  getSelectedOptions() {
    return Array.from(
      this.options.querySelectorAll(".global-product-quiz--checkbox:checked")
    ).map((el) => el.value);
  }

  highlightOptions() {
    this.options
      .querySelectorAll(".global-product-quiz--option-item")
      .forEach((el) => {
        let checkbox = el.querySelector(`input[type="checkbox"]`);
        el.classList[checkbox.checked ? "add" : "remove"]("highlight-option");
      });
  }

  /**
   * Displays the recommendation for the given handle.
   *
   * @param {string} recommendationHandle The handle of the recommendation to display.
   */
  showRecommendation(recommendationHandle) {
    this.resetVisibleRecommendation();

    const newRecommendation = this.querySelector(
      `.global-product-quiz--recommendation-item[data-product-quiz-recommendation-handle="${recommendationHandle}"]`
    );

    newRecommendation.dataset.productQuizShowRecommendation = true;
    this.querySelector(".global-product-quiz--recommendations").scrollIntoView({
      behavior: "smooth",
    });
  }

  /**
   * Resets any currently visible recommendation.
   */
  resetVisibleRecommendation() {
    const previousVisibleRecommendation = this.querySelector(
      `[data-product-quiz-show-recommendation="true"]`
    );
    if (!previousVisibleRecommendation) return;
    previousVisibleRecommendation.dataset.productQuizShowRecommendation = false;
  }

  handleHashChange() {
    if (window.location.hash === "#quiz") {
      const offset = 150;
      const elementPosition =
        this.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  }
}

customElements.define("global-product-quiz", GlobalProductQuiz);
